(() => {
  const shopRoot = window.Shopify?.routes?.root || '/';
  const defaultOfferLimits = new Map([
    ['vitamin-g-string-17', 2],
    ['seamless-snatched-vag-17', 1],
  ]);

  const getOfferKey = (item) =>
    item.properties && item.properties._conditional_offer;

  const isOfferItem = (item, offerKey) => getOfferKey(item) === offerKey;

  // Read the Liquid context on every check, including after drawer refreshes.
  // It is rendered even when the country restriction hides the offer card.
  const getStorefrontCountry = () =>
    String(
      document.querySelector('[data-conditional-cart-offer-context]')
        ?.dataset.countryCode || window.Shopify?.country || ''
    ).trim().toUpperCase();

  const offerIsAvailableInCountry = (offerKey) =>
    offerKey !== 'vitamin-g-string-17' || getStorefrontCountry() !== 'US';

  const getProductHandle = (item) => {
    if (item.handle) return item.handle;

    const productPath = String(item.url || '').split('/products/')[1] || '';
    return productPath.split('?')[0].split('/')[0];
  };

  const getRegularProductQuantity = (cart, productHandles) =>
    cart.items.reduce((quantity, item) => {
      if (getOfferKey(item)) return quantity;

      return productHandles.has(getProductHandle(item))
        ? quantity + item.quantity
        : quantity;
    }, 0);

  const offerEligibilityRules = new Map([
    [
      'vitamin-g-string-17',
      (cart) =>
        getRegularProductQuantity(cart, new Set(['snatched-vag-2-0'])) >= 3 ||
        getRegularProductQuantity(
          cart,
          new Set(['seamless-snatched-vag'])
        ) >= 3,
    ],
    [
      'seamless-snatched-vag-17',
      (cart) =>
        getRegularProductQuantity(
          cart,
          new Set([
            'snatched-vag-g-string',
            'snatched-vag-high-rise-g-string',
          ])
        ) >= 2,
    ],
  ]);

  const offerIsEligible = (cart, offerKey) => {
    if (!offerIsAvailableInCountry(offerKey)) return false;

    const eligibilityRule = offerEligibilityRules.get(offerKey);
    return eligibilityRule ? eligibilityRule(cart) : true;
  };

  const getIneligibleOfferLineKeys = (cart) =>
    cart.items
      .filter((item) => {
        const offerKey = getOfferKey(item);
        return (
          offerEligibilityRules.has(offerKey) &&
          !offerIsEligible(cart, offerKey)
        );
      })
      .map((item) => item.key);

  const getCart = async () => {
    const response = await fetch(`${shopRoot}cart.js`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('The cart could not be checked. Please try again.');
    }

    return response.json();
  };

  const getOfferLimits = () => {
    const limits = new Map(defaultOfferLimits);

    document
      .querySelectorAll('[data-offer-key][data-maximum-offer-quantity]')
      .forEach((element) => {
        const offerKey = element.dataset.offerKey;
        const maximum = Number(element.dataset.maximumOfferQuantity);

        if (offerKey && Number.isFinite(maximum) && maximum > 0) {
          limits.set(offerKey, maximum);
        }
      });

    return limits;
  };

  /*
   * The custom offer buttons perform their own checks, but the theme's native
   * cart +/- controls use a separate update route. This guard caps each offer
   * independently and totals different size/colour lines within that offer.
   */
  const installDrawerQuantityGuard = () => {
    if (window.__conditionalCartOfferQuantityGuardInstalled) return;
    window.__conditionalCartOfferQuantityGuardInstalled = true;

    let refreshTimer;
    let requestNumber = 0;
    let eligibilityUpdatePromise = null;

    const removeIneligibleOfferLines = async (cart) => {
      const lineKeys = getIneligibleOfferLineKeys(cart);
      if (!lineKeys.length) return cart;

      if (eligibilityUpdatePromise) return eligibilityUpdatePromise;

      eligibilityUpdatePromise = (async () => {
        const updates = Object.fromEntries(
          lineKeys.map((lineKey) => [lineKey, 0])
        );
        const response = await fetch(`${shopRoot}cart/update.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            updates,
            sections: 'cart-drawer,cart-bubble',
            sections_url: window.location.pathname,
          }),
        });
        const result = await response.json();

        if (!response.ok) {
          throw new Error(
            result.description ||
              result.message ||
              'An unavailable cart offer could not be removed.'
          );
        }

        document.dispatchEvent(
          new CustomEvent('conditional-cart-offer:removed', {
            bubbles: true,
            detail: { lineKeys },
          })
        );

        document.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { openCartDrawer: true },
          })
        );

        return result;
      })();

      try {
        return await eligibilityUpdatePromise;
      } finally {
        eligibilityUpdatePromise = null;
      }
    };

    const applyGuard = (cart) => {
      const limits = getOfferLimits();
      const groups = new Map();
      const offerItemByLineKey = new Map();

      cart.items.forEach((item) => {
        const offerKey = getOfferKey(item);
        const maximum = limits.get(offerKey);
        if (!offerKey || !maximum) return;

        if (!groups.has(offerKey)) {
          groups.set(offerKey, {
            maximum,
            quantity: 0,
          });
        }

        const group = groups.get(offerKey);
        group.quantity += item.quantity;
        offerItemByLineKey.set(item.key, { group, item });
      });

      document
        .querySelectorAll('quantity-selector.cart-update[data-id]')
        .forEach((quantitySelector) => {
          const line = offerItemByLineKey.get(quantitySelector.dataset.id);
          if (!line) return;

          const { group, item } = line;
          const input = quantitySelector.querySelector(
            'input[name="updates[]"]'
          );
          const plusButton = quantitySelector.querySelector('.plus');
          if (!input || !plusButton) return;

          if (
            !Object.prototype.hasOwnProperty.call(
              input.dataset,
              'conditionalOfferInventoryMaximum'
            )
          ) {
            input.dataset.conditionalOfferInventoryMaximum =
              input.getAttribute('max') || '';
          }

          const inventoryMaximumAttribute =
            input.dataset.conditionalOfferInventoryMaximum;
          const inventoryMaximum = Number(inventoryMaximumAttribute);
          const hasInventoryMaximum =
            inventoryMaximumAttribute !== '' &&
            Number.isFinite(inventoryMaximum);
          const remainingAllowance = Math.max(
            0,
            group.maximum - group.quantity
          );
          const maximumForThisLine = item.quantity + remainingAllowance;
          const effectiveMaximum = hasInventoryMaximum
            ? Math.min(inventoryMaximum, maximumForThisLine)
            : maximumForThisLine;
          const limitReached = group.quantity >= group.maximum;

          quantitySelector.dataset.conditionalOfferGuard = 'true';
          input.max = String(effectiveMaximum);
          input.dataset.conditionalOfferMaximum = String(effectiveMaximum);

          if (limitReached || Number(input.value) >= effectiveMaximum) {
            plusButton.disabled = true;
            plusButton.classList.add('disabled');
            plusButton.setAttribute('aria-disabled', 'true');
            plusButton.dataset.conditionalOfferDisabled = 'true';
          } else if (plusButton.dataset.conditionalOfferDisabled === 'true') {
            plusButton.disabled = false;
            plusButton.classList.remove('disabled');
            plusButton.removeAttribute('aria-disabled');
            delete plusButton.dataset.conditionalOfferDisabled;
          }
        });
    };

    const refreshGuard = async () => {
      const currentRequest = ++requestNumber;

      try {
        let cart = await getCart();
        if (currentRequest !== requestNumber) return;

        cart = await removeIneligibleOfferLines(cart);
        if (currentRequest !== requestNumber) return;

        applyGuard(cart);
      } catch (error) {
        console.warn('Conditional cart offer guard:', error);
      }
    };

    const scheduleRefresh = (delay = 60) => {
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(refreshGuard, delay);
    };

    document.addEventListener(
      'click',
      (event) => {
        const plusButton = event.target.closest(
          'quantity-selector[data-conditional-offer-guard="true"] .plus'
        );

        if (!plusButton || !plusButton.disabled) return;

        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true
    );

    document.addEventListener(
      'change',
      (event) => {
        const input = event.target.closest(
          'quantity-selector[data-conditional-offer-guard="true"] input[name="updates[]"]'
        );
        if (!input) return;

        const maximum = Number(input.dataset.conditionalOfferMaximum);
        if (Number.isFinite(maximum) && Number(input.value) > maximum) {
          input.value = String(maximum);
        }

        scheduleRefresh(700);
      },
      true
    );

    [
      'cart:refresh',
      'cart:updated',
      'conditional-cart-offer:added',
    ].forEach((eventName) => {
      document.addEventListener(eventName, () => scheduleRefresh(400));
    });

    const observer = new MutationObserver((mutations) => {
      const drawerChanged = mutations.some((mutation) =>
        [...mutation.addedNodes].some(
          (node) =>
            node.nodeType === Node.ELEMENT_NODE &&
            (node.matches?.(
              'quantity-selector.cart-update, cart-drawer, [data-conditional-cart-offer-context]'
            ) ||
              node.querySelector?.(
                'quantity-selector.cart-update, [data-conditional-cart-offer-context]'
              ))
        )
      );

      if (drawerChanged) scheduleRefresh();
    });

    const startObserver = () => {
      observer.observe(document.body, { childList: true, subtree: true });
      scheduleRefresh(0);
    };

    if (document.body) {
      startObserver();
    } else {
      document.addEventListener('DOMContentLoaded', startObserver, {
        once: true,
      });
    }
  };

  installDrawerQuantityGuard();

  if (customElements.get('conditional-cart-offer')) return;

  class ConditionalCartOffer extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.variantSelect = this.querySelector(
        '[data-conditional-offer-variant]'
      );
      this.addButton = this.querySelector('[data-conditional-offer-add]');
      this.buttonLabel = this.querySelector(
        '[data-conditional-offer-button-label]'
      );
      this.originalPrice = this.querySelector(
        '[data-conditional-offer-original-price]'
      );
      this.offerPrice = this.querySelector('[data-conditional-offer-price]');
      this.colour = this.querySelector('[data-conditional-offer-colour]');
      this.errorMessage = this.querySelector('[data-conditional-offer-error]');
      this.currency = this.dataset.currencyCode || 'AUD';
      this.locale =
        this.dataset.locale || document.documentElement.lang || 'en-AU';
      this.offerKey = this.dataset.offerKey || '';
      this.offerProductTitle = this.dataset.offerProductTitle || 'offer item';
      this.maximumOfferQuantity = Math.max(
        1,
        Number(this.dataset.maximumOfferQuantity || 1)
      );
      this.kachingConfig = {
        deal: this.dataset.kachingDeal || '',
        pp: Number(this.dataset.kachingParentProduct || 0),
        main: this.dataset.kachingMain !== 'false',
        market: Number(this.dataset.kachingMarket || 0),
        v: this.dataset.kachingVersion || '',
        bid: this.dataset.kachingBundleId || '',
      };

      if (!this.variantSelect || !this.addButton) return;

      this.variantSelect.addEventListener('change', () =>
        this.updateVariantDisplay()
      );
      this.addButton.addEventListener('click', () => this.addOffer());
      this.updateVariantDisplay();
    }

    selectedOption() {
      return this.variantSelect.options[this.variantSelect.selectedIndex];
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;
      const hasCents = Math.round(amount * 100) % 100 !== 0;

      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
          minimumFractionDigits: hasCents ? 2 : 0,
          maximumFractionDigits: 2,
        }).format(amount);
      } catch (error) {
        return `${this.currency} ${amount.toFixed(hasCents ? 2 : 0)}`;
      }
    }

    updateVariantDisplay() {
      const option = this.selectedOption();
      if (!option) return;

      const originalPrice = Number(option.dataset.originalPriceCents || 0);
      const offerPrice = Number(option.dataset.offerPriceCents || 0);
      const addLabel = this.addButton.dataset.addLabel || 'Add for';

      if (this.originalPrice) {
        this.originalPrice.textContent = this.formatMoney(originalPrice);
      }
      if (this.offerPrice) {
        this.offerPrice.textContent = this.formatMoney(offerPrice);
      }
      if (this.colour) {
        this.colour.textContent = option.dataset.colour || '';
      }
      if (this.buttonLabel) {
        this.buttonLabel.textContent = `${addLabel} ${this.formatMoney(
          offerPrice
        )}`;
      }

      this.clearError();
    }

    setLoading(loading) {
      this.addButton.disabled = loading;
      this.addButton.setAttribute('aria-busy', String(loading));
      this.toggleAttribute('data-loading', loading);
    }

    clearError() {
      if (!this.errorMessage) return;
      this.errorMessage.hidden = true;
      this.errorMessage.textContent = '';
    }

    showError(message) {
      if (!this.errorMessage) return;
      this.errorMessage.textContent = message;
      this.errorMessage.hidden = false;
    }

    kachingConfigIsValid() {
      return Boolean(
        this.kachingConfig.deal &&
          this.kachingConfig.pp &&
          this.kachingConfig.market &&
          this.kachingConfig.v &&
          this.kachingConfig.bid
      );
    }

    maximumMessage() {
      if (this.maximumOfferQuantity === 1) {
        return `The ${this.offerProductTitle} offer has already been claimed.`;
      }

      return `A maximum of ${this.maximumOfferQuantity} ${this.offerProductTitle} offer pairs can be purchased per order.`;
    }

    async addOffer() {
      const option = this.selectedOption();
      const variantId = Number(option?.value || 0);

      if (
        !variantId ||
        !this.offerKey ||
        this.addButton.getAttribute('aria-busy') === 'true'
      ) {
        return;
      }

      this.clearError();
      this.setLoading(true);

      try {
        if (!this.kachingConfigIsValid()) {
          throw new Error('This offer is not fully configured yet.');
        }

        const cart = await getCart();

        if (!offerIsEligible(cart, this.offerKey)) {
          document.dispatchEvent(
            new CustomEvent('cart:refresh', {
              bubbles: true,
              detail: { openCartDrawer: true },
            })
          );
          throw new Error(
            !offerIsAvailableInCountry(this.offerKey)
              ? 'This offer is not available in your selected country.'
              : 'This offer is no longer available because its qualifying products have been removed.'
          );
        }

        const existingOfferQuantity = cart.items.reduce(
          (total, item) =>
            isOfferItem(item, this.offerKey)
              ? total + item.quantity
              : total,
          0
        );

        if (existingOfferQuantity >= this.maximumOfferQuantity) {
          throw new Error(this.maximumMessage());
        }

        const response = await fetch(`${shopRoot}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1,
                properties: {
                  _conditional_offer: this.offerKey,
                  __kaching_bundles: JSON.stringify(this.kachingConfig),
                },
              },
            ],
            sections: 'cart-drawer,cart-bubble',
            sections_url: window.location.pathname,
          }),
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(
            result.description ||
              result.message ||
              'This option could not be added.'
          );
        }

        document.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { openCartDrawer: true },
          })
        );

        document.dispatchEvent(
          new CustomEvent('conditional-cart-offer:added', {
            bubbles: true,
            detail: { offerKey: this.offerKey, variantId },
          })
        );
      } catch (error) {
        this.showError(
          error.message || 'Something went wrong. Please try again.'
        );
        this.setLoading(false);
      }
    }
  }

  customElements.define('conditional-cart-offer', ConditionalCartOffer);
})();
